import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, forwardRef, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { IProductDataV2 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { DfEventService } from '@df/core/service/df-event';
import { Status } from '@df/core/status';
import { AbService } from '@df/module-ab/ab.service';
import { DfImageDirective } from '@df/module-image/df-image.directive';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { Basket, IAddToBasketData } from '@df/session/api/basket/basket';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { Subscription } from 'rxjs/internal/Subscription';
import { ProductDecorator } from '../catalog/model/product/product.decorator';
import { ICmsOutletBlockComponent } from '../catalog/outlet-block/icms-outlet-block.component';
import { ProductGiftBoxComponent } from '../catalog/ui/product/gift/box/product-gift-box.component';
import { ResultComponent } from '../ui/api/result.component';
import { strapsUpsellCarouselAbConfig } from './straps-upsell-carousel/basket-straps-upsell-carousel.component';

export const BASKET_ADD_NOTICE_MODAL_UPDATE_EVENT_NAME = 'basket-add-notice-modal.update.request';

export interface IBasketAddNoticeModalData {
    basketItem: BasketItem;
    request: IAddToBasketData;
}

declare module '@df/core/service/df-event' {
    interface IEventType {
        basketAddNoticeClose: DfEventConfig<'basketAddNoticeClose', {}, void | undefined>;
    }
}

export const BASKET_ADD_NOTICE_CLOSE_EVENT_NAME = 'basketAddNoticeClose';

@Component({
    selector: 'basket-add-notice-modal',
    templateUrl: './basket-add-notice.html',
    standalone: true,
    imports: [
        CommonModule,
        DfImageDirective,
        ICmsClientModule,
        ICmsOutletBlockComponent,
        OptionLabelPipe,
        PriceComponent,
        forwardRef(() => ProductGiftBoxComponent),
        ResultComponent,
        RouterModule,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasketAddNoticeModalComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();
    addedItem!: BasketItem;
    source?: ProductDecorator<IProductDataV2>;
    price?: number;
    orgPrice?: number;
    addStatus = new Status();
    giftBoxSelection = false;
    protected basketAddNotifyTimeout?: number;

    protected app = inject(App);
    protected abService = inject(AbService);
    protected dfEventService = inject(DfEventService);
    protected readonly timeoutService = injectTimeoutService();

    constructor(
        @Inject(MODAL_DATA)
        private basketAddNoticeData: IBasketAddNoticeModalData,
        private modalRef: ModalRef<BasketAddNoticeModalComponent>,
        private changeDetectorRef: ChangeDetectorRef
    ) {
        this.onDataUpdate(basketAddNoticeData);
    }

    ngOnInit() {
        const showStrapsUpsellCarousel =
            this.abService.getVariant(strapsUpsellCarouselAbConfig.experimentName) === strapsUpsellCarouselAbConfig.showFor;

        const showUpsellCarousel = !!this.addedItem.product?.data?.upsell_carousel;

        if (!this.app.client.isCms && !showUpsellCarousel && !showStrapsUpsellCarousel) {
            this.basketAddNotifyTimeout = this.timeoutService.setTimeout(() => {
                this.close();
            }, 5000);
        }

        this.subscriptions.add(
            this.app.events.on(BASKET_ADD_NOTICE_MODAL_UPDATE_EVENT_NAME).subscribe((basketAddNoticeData: IBasketAddNoticeModalData) => {
                this.onDataUpdate(basketAddNoticeData);
                this.detectChanges();
            })
        );
        this.subscriptions.add(this.app.meta.routerEvents.subscribe(() => this.close()));
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    get basket(): Basket {
        return this.app.customer.basket;
    }

    close() {
        this.dfEventService.dispatch({
            id: BASKET_ADD_NOTICE_CLOSE_EVENT_NAME,
            data: {},
            action: () => {
                if (this.basketAddNotifyTimeout) {
                    clearTimeout(this.basketAddNotifyTimeout);
                    this.basketAddNotifyTimeout = undefined;
                }

                this.modalRef?.close();
            }
        });
    }

    /**
     * Gift box
     */
    giftBoxSelectionChangeHandle(value: boolean) {
        const productId = this.basketAddNoticeData.request.id;
        const giftBox = this.app.resources.product.get(productId) as ProductDecorator<IProductDataV2>;

        if (value && giftBox) {
            this.basket.add(this.addStatus, giftBox.data?.gift_box_product_id, () => {
                this.giftBoxSelection = false;
                this.detectChanges();
            });
        }
    }

    protected onDataUpdate(basketAddNoticeData: IBasketAddNoticeModalData): void {
        if (basketAddNoticeData.basketItem) {
            const addedItem = basketAddNoticeData.basketItem;
            const qty = basketAddNoticeData.request.qty || addedItem.qty;
            this.price = addedItem.price * qty;
            this.orgPrice = addedItem.orgPrice ? addedItem.orgPrice * qty : undefined;
            this.addedItem = addedItem;
        }

        if (basketAddNoticeData.request) {
            const request = basketAddNoticeData.request;
            const product = this.app.resources.product.get(request.id) as ProductDecorator<IProductDataV2>;

            if (product) {
                this.source = product;
            }
        }
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }
}
